<template>
  <div
    class="the-a01-point-notification-carousel"
    :style="{
      '--site-color': colorCode,
      '--swiper-pagination-color': colorCode,
      '--swiper-pagination-bullet-inactive-color': '#dadada',
      '--swiper-pagination-bullet-inactive-opacity': 1
    }"
  >
    <template v-if="useCarousel">
      <div
        class="the-a01-point-notification-carousel__carousel c-swiper c-swiper--outer_pagination"
      >
        <Swiper
          :loop="true"
          :autoplay="{ delay: 5000 }"
          :pagination="{ clickable: true }"
        >
          <SwiperSlide
            v-for="(pointNoticeCard, key) in pointNoticeCards"
            :key="key"
          >
            <A01PointNotificationPanelThanks
              v-if="thanksPointTypes.includes(pointNoticeCard.transactionType)"
              :giver="giver(pointNoticeCard)"
              :point="pointNoticeCard.points"
              :url="urls(pointNoticeCard)"
            />
            <A01PointNotificationPanelInvested
              v-if="grantPointTypes.includes(pointNoticeCard.transactionType)"
              :title="pointNoticeCard.grantCriteriaName"
              :invested-reason="pointNoticeCard.grantCriteriaName"
              :point="pointNoticeCard.points"
              :url="urls(pointNoticeCard)"
            />
            <A01PointNotificationPanelThanksOnlyPoint
              v-if="thanksOnlyPointTypes.includes(pointNoticeCard.transactionType)"
              :point="pointNoticeCard.points"
              :url="urls(pointNoticeCard)"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </template>
    <template v-else>
      <template v-for="(pointNoticeCard, key) in pointNoticeCards">
        <A01PointNotificationPanelThanks
          v-if="thanksPointTypes.includes(pointNoticeCard.transactionType)"
          :key="key"
          :giver="giver(pointNoticeCard)"
          :point="pointNoticeCard.points"
          :url="urls(pointNoticeCard)"
        />
        <A01PointNotificationPanelInvested
          v-if="grantPointTypes.includes(pointNoticeCard.transactionType)"
          :key="key"
          :title="pointNoticeCard.grantCriteriaName"
          :invested-reason="pointNoticeCard.grantCriteriaName"
          :point="pointNoticeCard.points"
          :url="urls(pointNoticeCard)"
        />
        <A01PointNotificationPanelThanksOnlyPoint
          v-if="thanksOnlyPointTypes.includes(pointNoticeCard.transactionType)"
          :key="key"
          :point="pointNoticeCard.points"
          :url="urls(pointNoticeCard)"
        />
      </template>
    </template>
  </div>
</template>

<script lang="ts">
import A01PointNotificationPanelThanks from "@/general/components/pages/parts/Top/A01PointNotificationPanelThanks.vue";
import A01PointNotificationPanelInvested from "@/general/components/pages/parts/Top/A01PointNotificationPanelInvested.vue";
import A01PointNotificationPanelThanksOnlyPoint from "@/general/components/pages/parts/Top/A01PointNotificationPanelThanksOnlyPoint.vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Swiper as SwiperClass, Pagination, Mousewheel, Autoplay } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import { Options, Vue } from "vue-class-component";
import { mapState } from "vuex";
import "swiper/swiper-bundle.css";
import { PointNoticeCard } from "@/general/store/models";
import CONST_DATA from "@/general/mixins/const/constData";
import auth from "@/general/store/modules/auth";
import commons from "@/common/store/modules/commons";

SwiperClass.use([Pagination, Mousewheel, Autoplay]);

@Options({
  props: {
    pointNoticeCards: { type: Array, required: true }
  },
  components: {
    A01PointNotificationPanelThanks,
    A01PointNotificationPanelInvested,
	A01PointNotificationPanelThanksOnlyPoint,
    Swiper,
    SwiperSlide
  },
  computed: {
    ...mapState({
      storeColorCode: () => commons.colorCode
    })
  }
})
export default class TheA01PointNotificationCarousel extends Vue {
  pointNoticeCards!: PointNoticeCard[];
  storeColorCode!: string;

  thanksPointTypes = [50];
  grantPointTypes = [20, 90, 100];
  thanksOnlyPointTypes = [40];

  swiperOption = {
    loop: true,
    speed: 1000,
    autoplay: {
      delay: 5000
    },
    pagination: {
      el: ".swiper-pagination",
      clickable: true
    }
  };

  get isGroupBudget() {
    return commons?.userInfo.groupBudgetMode;
  }

  get colorCode() {
    if (this.isGroupBudget) {
      return "#fc9014";
    }
    return this.storeColorCode;
  }

  get useCarousel() {
    return this.pointNoticeCards.length > 1;
  }

  urls(pointNoticeCard: PointNoticeCard) {
    if (this.thanksPointTypes.includes(pointNoticeCard.transactionType)) {
      return `${CONST_DATA.SITE_MAP.DETAILS_GRANT_THANKS}?id=${pointNoticeCard.id}`;
    }
    return `${CONST_DATA.SITE_MAP.DETAILS_GRANT_POINTS}?id=${pointNoticeCard.id}`;
  }

  giver(pointNoticeCard: PointNoticeCard) {
    const userInfo = auth.user;
    return {
      id: userInfo?.user_id,
      user_image: pointNoticeCard.fromUserAvatar,
      user_name: pointNoticeCard.fromUserName
    };
  }
}
</script>

<style lang="scss" scoped>
.the-a01-point-notification-carousel {
  width: 100%;

  :deep(.p-point-notification-panel-small) {
    width: 100%;
  }

  &__carousel {
    position: relative;
    width: 100%;
  }

  :deep(.swiper) {
    padding-bottom: rem(18);
  }

  :deep(.swiper-slide) {
    height: auto;
    display: flex;
  }

  :deep(.swiper-pagination) {
    bottom: 0;
  }

  :deep(.swiper-pagination-bullet) {
    width: rem(8);
    height: rem(8);
    margin: 0 rem(2);
    background: #dadada;
    opacity: 1;
  }

  :deep(.swiper-pagination-bullet-active) {
    background: var(--site-color);
  }
}
</style>
